import LocationLanding from './LocationLanding';
import { buildBreadcrumb, buildFAQPage, SITE_URL } from '@/lib/seo';

const SLUG = 'ambulance-tronget-le-montet';
const CANONICAL = `${SITE_URL}/${SLUG}`;

const COMMUNES = [
  { name: 'Tronget',           lat: 46.4483, lng: 3.0522, main: true },
  { name: 'Le Montet',         lat: 46.3878, lng: 2.9294, main: true },
  { name: "Noyant-d'Allier",   lat: 46.4839, lng: 3.0625 },
  { name: 'Cressanges',        lat: 46.4700, lng: 3.0136 },
  { name: 'Rocles',            lat: 46.4292, lng: 2.9317 },
  { name: 'Saint-Sornin',      lat: 46.4325, lng: 3.0625 },
  { name: 'Deux-Chaises',      lat: 46.3697, lng: 2.9781 },
  { name: 'Châtillon',         lat: 46.5058, lng: 2.9367 },
  { name: 'Gipcy',             lat: 46.4497, lng: 2.9564 },
];

const FAQS = [
  { q: 'Comment réserver un transport sanitaire à Tronget ou Le Montet ?', a: `Appelez notre équipe au 04 70 47 10 30 ou remplissez le formulaire ci-dessus. Nous vous rappelons pour confirmer les horaires, le mode de transport et les modalités administratives.` },
  { q: 'Quelles communes desservez-vous depuis Tronget / Le Montet ?', a: `Nous couvrons principalement Tronget, Le Montet, Noyant-d'Allier, Cressanges, Rocles, Saint-Sornin, Deux-Chaises, Châtillon et Gipcy. Nous intervenons plus largement dans le centre et le sud-ouest de l'Allier selon disponibilité.` },
  { q: 'Une prescription médicale est-elle obligatoire pour un remboursement CPAM ?', a: `Oui. Pour un remboursement Assurance Maladie, une prescription médicale (bon de transport CERFA n°50742*04) délivrée par votre médecin est obligatoire, sauf urgence vitale. Elle indique le mode de transport (ambulance, VSL, taxi conventionné).` },
  { q: 'Vos véhicules sont-ils conventionnés CPAM ?', a: `Oui. Toutes nos ambulances et nos VSL sont conventionnés par l'Assurance Maladie. Nous appliquons la prise en charge directe (tiers payant) pour les patients éligibles.` },
  { q: 'Quels services proposez-vous ?', a: `Transport en ambulance et en VSL pour hospitalisations, consultations, dialyses, chimiothérapie, radiothérapie, rééducation, sorties d'hôpital, transferts inter-établissements et longue distance.` },
  { q: 'Intervenez-vous à domicile ?', a: `Oui, nous venons vous chercher à votre domicile (ou lieu de départ) et vous accompagnons jusqu'à votre destination : hôpital, clinique ou centre de soins. Nous assurons aussi le retour à domicile.` },
];

const CONFIG = {
  slug: SLUG,
  brandName: 'Ambulance Tronget – Le Montet',
  brandNameShort: 'Ambulance Tronget',
  brandTitle: 'Ambulance Tronget',
  brandSubtitle: 'Le Montet · Village Thibault',
  kicker: "Notre implantation dans le centre et le sud-ouest de l'Allier",
  zoneLabel: 'Tronget · Le Montet · centre/sud-ouest Allier',
  zoneLabelShort: 'Tronget / Le Montet',
  address: 'Village Thibault, 03240 Tronget',
  heroTitle: (<>Ambulance et VSL à <span className="text-brand-700">Tronget</span> et <span className="text-brand-700">Le Montet</span></>),
  description: (<>Transport sanitaire conventionné pour <strong className="text-anthracite-900">consultations, hospitalisations, dialyses, rééducation, retours à domicile</strong> et transports programmés dans le centre et le sud-ouest de l'Allier.</>),
  phoneDisplay: '04 70 47 10 30',
  phoneTel: '+33470471030',
  whyChooseTitle: "Une équipe locale, ancrée dans le centre et le sud-ouest de l'Allier",
  serviceAreaTitle: 'Nous intervenons sur tout le secteur de Tronget / Le Montet',
  serviceAreaDesc: "Tronget, Le Montet, Noyant-d'Allier, Cressanges, Rocles, Saint-Sornin, Deux-Chaises, Châtillon, Gipcy et communes environnantes. Nous intervenons plus largement dans le centre et le sud-ouest de l'Allier selon disponibilité.",
  communes: COMMUNES,
  mapCenter: [46.4483, 3.0100],
  mapRadius: 14000,
  mapZoom: 11,
  canonical: CANONICAL,
  ogImage: 'https://customer-assets.emergentagent.com/job_ambulances-beaulon/artifacts/5zctf2x4_file_000000005b88720a8aa856385a7438f6.png',
  ogType: 'website',
  seoTitle: 'Ambulance Tronget Le Montet | VSL & Transport sanitaire Allier',
  seoDescription: "Ambulance et VSL à Tronget et Le Montet (Village Thibault). Transport sanitaire conventionné CPAM dans le centre et le sud-ouest de l'Allier. Réservez au 04 70 47 10 30.",
  seoKeywords: "ambulance tronget, ambulance le montet, vsl tronget, vsl le montet, ambulance noyant d'allier, transport sanitaire sud allier, ambulance conventionnée tronget, ambulance cressanges, ambulance rocles, transport médical centre allier",
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': ['MedicalBusiness', 'LocalBusiness'],
      '@id': `${CANONICAL}#business`,
      name: 'Ambulance Tronget – Le Montet',
      alternateName: ['Ambulance Le Montet'],
      description: "Transport sanitaire conventionné en ambulance et VSL à Tronget, Le Montet et dans le centre et le sud-ouest de l'Allier.",
      url: CANONICAL,
      telephone: '+33470471030',
      priceRange: '€€',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Village Thibault',
        addressLocality: 'Tronget',
        postalCode: '03240',
        addressRegion: 'Allier',
        addressCountry: 'FR',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 46.4483, longitude: 3.0522 },
      areaServed: COMMUNES.map((c) => ({ '@type': 'City', name: c.name })),
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
        opens: '00:00', closes: '23:59',
      }],
      parentOrganization: { '@type': 'Organization', name: 'Groupe Ambulances Beaulon', url: SITE_URL },
      medicalSpecialty: 'MedicalTransport',
    },
    buildBreadcrumb([
      { name: 'Accueil', url: SITE_URL + '/' },
      { name: 'Nos implantations', url: SITE_URL + '/implantations' },
      { name: 'Ambulance Tronget – Le Montet', url: CANONICAL },
    ]),
    buildFAQPage(FAQS),
  ],
};

export default function AmbulanceTrongetLeMontet() {
  return <LocationLanding config={CONFIG} />;
}
